"use client";
import { countries } from "@/data/rangers/countries";
import { useUserDetails } from "@/hooks/rangers/useUserDetails";
import { Check, Close, FilterList, Remove } from "@mui/icons-material";
import {
  Autocomplete,
  Badge,
  Button,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { ChangeEvent, useMemo, useState } from "react";
import SimpleDrawer from "../general/SimpleDrawer";

type props = {
  filter: (text: string, country: string, additionalFilters?: { [key: string]: boolean | null }) => void;
  additionalFilters?: { [key: string]: { name: string; value: boolean | null } };
};

export default function TableFilter({ filter, additionalFilters }: props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [country, setCountry] = useState("");
  const [additionalFilterValues, setAdditionalFilterValues] = useState<{ [key: string]: boolean | null }>(
    additionalFilters
      ? Object.fromEntries(Object.entries(additionalFilters).map(([key, value]) => [key, value.value]))
      : {}
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: userData } = useUserDetails();

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => {
      if (a === userData?.country) return -1;
      if (b === userData?.country) return 1;
      return a.localeCompare(b);
    });
  }, [userData?.country]);

  const activeFilterCount = useMemo(
    () => Object.values(additionalFilterValues).filter((value) => value != null).length,
    [additionalFilterValues]
  );

  const AdditionalFilter = ({ parameterKey, name }: { parameterKey: string; name: string }) => {
    return (
      <Stack
        direction={"column"}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1
        }}
      >
        <Typography variant="h3">{name}</Typography>
        <ToggleButtonGroup
          size="small"
          value={additionalFilterValues[parameterKey]}
          exclusive
          onChange={(e, value: boolean | null) => {
            const newValues = { ...additionalFilterValues, [parameterKey]: value };
            setAdditionalFilterValues(newValues);
            filter(searchTerm, country, newValues);
          }}
        >
          <ToggleButton value={null} size="small" color="standard">
            <Remove />
          </ToggleButton>
          <ToggleButton value={false} size="small" color="error">
            <Close />
          </ToggleButton>
          <ToggleButton value={true} size="small" color="success">
            <Check />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    );
  };

  const handleSearchTermChange = (text: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchTerm(text.currentTarget.value);
    filter(text.currentTarget.value, country, additionalFilterValues);
  };

  const handleCountryChange = (_e: any, value: string) => {
    setCountry(value);
    filter(searchTerm, value, additionalFilterValues);
  };

  return (
    <Stack>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{
          mb: 1,
          gap: 1
        }}
      >
        <TextField
          sx={{ flex: 2 }}
          fullWidth
          label="Search"
          value={searchTerm}
          onChange={handleSearchTermChange}
        />
        <Stack
          direction="row"
          sx={{
            gap: 1,
            flex: 1
          }}
        >
          <Autocomplete
            options={sortedCountries}
            value={country}
            title="Country"
            onChange={handleCountryChange}
            renderInput={(params) => <TextField {...params} label="Country" />}
            sx={{ flex: 1 }}
          />
          {additionalFilters && (
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setFiltersOpen(true)}
                sx={{ height: "100%", alignSelf: "center" }}
              >
                Filters
              </Button>
            </Badge>
          )}
        </Stack>
      </Stack>
      <SimpleDrawer open={filtersOpen} setOpen={setFiltersOpen} title="Filters">
        <Stack
          direction="column"
          sx={{
            m: 2,
            flex: 1,
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Stack>
            <Stack
              direction="column"
              sx={{
                gap: 2
              }}
            >
              {additionalFilters &&
                Object.entries(additionalFilters).map(([key, value]) => (
                  <AdditionalFilter key={key} parameterKey={key} name={value.name} />
                ))}
            </Stack>
            <Divider sx={{ my: 3 }} />
          </Stack>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const reset = additionalFilters
                ? Object.fromEntries(
                    Object.entries(additionalFilters).map(([key, value]) => [key, value.value])
                  )
                : {};
              setAdditionalFilterValues(reset);
              filter(searchTerm, country, reset);
            }}
          >
            Reset
          </Button>
        </Stack>
      </SimpleDrawer>
    </Stack>
  );
}
